import { useCallback, useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { ColumnDef } from '@tanstack/react-table';
import { EToasterStatus, showToast } from 'utils/showToast';

import Button from 'components/Button/Button';
import DialogModal from 'components/DialogModal/DialogModal';
import Input from 'components/Input/Input';
import { TableView } from 'components/TableView/TableView';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger
} from 'components/ui/dropdown-menu';

import { CouponFormValues } from '../../../types';

import { handleExportCsv } from './utils';

interface CouponsAllowedPlayersTableProps {
  couponFormValues: CouponFormValues;
  setFieldValue: (field: string, value: any) => void;
}

interface AllowedPlayerRow {
  playerId: string;
}

export default function CouponsAllowedPlayersTable({
  couponFormValues,
  setFieldValue
}: CouponsAllowedPlayersTableProps) {
  const { allowedPlayers } = couponFormValues;
  const [searchTerm, setSearchTerm] = useState('');
  const [isImportCSVModalOpen, setIsImportCSVModalOpen] = useState(false);

  const handleDelete = useCallback(
    (playerId: string) => {
      const currentPlayers = allowedPlayers || [];
      const updatedPlayers = currentPlayers.filter((id) => id !== playerId);
      setFieldValue('allowedPlayers', updatedPlayers);
    },
    [allowedPlayers, setFieldValue]
  );

  const tableColumns: ColumnDef<AllowedPlayerRow>[] = useMemo(
    () => [
      {
        accessorKey: 'playerId',
        header: 'Player ID',
        enableSorting: true
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          return (
            <div className="flex justify-end w-full">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <FontAwesomeIcon icon="fa-solid fa-ellipsis-vertical" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuContent align="end" className="bg-base-popover">
                    <DropdownMenuItem
                      className="cursor-pointer hover:bg-[#F4F4F5]"
                      onClick={() => handleDelete(row.original.playerId)}
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenuPortal>
              </DropdownMenu>
            </div>
          );
        }
      }
    ],
    [handleDelete]
  );

  const tableData = useMemo(() => {
    const allPlayers = allowedPlayers || [];
    const filtered = searchTerm
      ? allPlayers.filter((playerId) =>
          playerId.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : allPlayers;

    return filtered.map((playerId: string) => ({
      playerId
    }));
  }, [allowedPlayers, searchTerm]);

  const handleImportFile = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv, text/csv';
    fileInput.style.display = 'none';

    fileInput.onchange = (event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        console.log('Selected file:', file);

        const reader = new FileReader();

        reader.onload = (e) => {
          try {
            const text = e.target?.result as string;
            if (typeof text !== 'string') {
              throw new Error('Failed to read file content as text.');
            }

            const playerIds = text
              .split('\n')
              .map((line) => line.trim())
              .filter((line) => line.length > 0)
              .slice(1); // Skip the header row

            if (playerIds.length === 0) {
              showToast({
                message:
                  'CSV file is empty or contains no valid player IDs after skipping the header.',
                status: EToasterStatus.ERROR
              });
              return;
            }

            if (playerIds.length > 10000) {
              showToast({
                message: 'Cannot import more than 10,000 player IDs.',
                status: EToasterStatus.ERROR
              });
              return;
            }

            setFieldValue('allowedPlayers', playerIds);

            showToast({
              message: `Imported ${playerIds.length} player IDs.`,
              status: EToasterStatus.SUCCESS
            });
            setIsImportCSVModalOpen(false);
          } catch (error: any) {
            console.error('Error processing CSV:', error);
            showToast({
              message: `Error processing CSV: ${error.message || 'Unknown error'}`,
              status: EToasterStatus.ERROR
            });
          }
        };

        reader.onerror = () => {
          console.error('Error reading file:', reader.error);
          showToast({
            message: 'Error reading file.',
            status: EToasterStatus.ERROR
          });
        };

        reader.readAsText(file);
      }
      document.body.removeChild(fileInput);
    };

    document.body.appendChild(fileInput);
    fileInput.click();
  };

  return (
    <>
      <div className="flex flex-col">
        <div className="flex justify-between items-start">
          <div className="flex flex-col gap-1">
            <h3 className="text-lg font-medium text-base-foreground">Whitelist</h3>
            <p className="text-base" style={{ color: 'var(--base-muted-foreground)' }}>
              Import a file with player IDs to limit this coupon to specific players.
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <Button
              variant="outline"
              onClick={() => setIsImportCSVModalOpen(true)}
            >
              <FontAwesomeIcon icon="fa-light fa-download" className="mr-2" />
              Import CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExportCsv(allowedPlayers)}
            >
              <FontAwesomeIcon icon="fa-light fa-upload" className="mr-2" />
              Export CSV
            </Button>
          </div>
        </div>
        {allowedPlayers?.length > 0 && (
          <div className="w-[323px] my-5">
            <Input
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              leftIcon={<FontAwesomeIcon icon="fa-regular fa-magnifying-glass" />}
            />
          </div>
        )}

        {tableData.length > 0 ? (
          <TableView<AllowedPlayerRow>
            data={tableData}
            columns={tableColumns}
            isLoading={false}
            rowIdIdentifier="playerId"
            containerClassName="min-h-0 max-h-none overflow-visible [&>div]:overflow-visible"
            wrapperClassName="p-0 border-b rounded-lg overflow-hidden"
            showPagination={true}
            rowsPerPage={5}
          />
        ) : (
          <div className="border border-base-border rounded-lg p-8 text-center text-sm mt-5" style={{ color: 'var(--base-muted-foreground)' }}>
            {allowedPlayers?.length > 0 ? (
              'No players match your search'
            ) : (
              <>
                All players can currently redeem this coupon. To limit
                access, add players to the allow list.
                <br />
                Click Export CSV to create a template file.
              </>
            )}
          </div>
        )}
      </div>
      {isImportCSVModalOpen && (
        <DialogModal
          isOpen={isImportCSVModalOpen}
          title={
            allowedPlayers?.length > 0
              ? 'Replace existing list'
              : 'Add allowed players'
          }
          description={
            allowedPlayers?.length > 0
              ? 'Uploading this file will overwrite your current whitelist.'
              : 'Uploading this file will add the players to your allowed players list'
          }
          modalActions={{
            onConfirm: {
              label: 'Upload',
              action: () => handleImportFile(),
              variant: 'default'
            },
            onClose: {
              label: 'Cancel',
              action: () => setIsImportCSVModalOpen(false),
              variant: 'outline'
            }
          }}
        />
      )}
    </>
  );
}
